// caiet UC91-03 as CF-66 changed it: "seriile se disting prin marcaje diferite și
// legendă". The marker a series draws its points with, and the same marker on a short
// line for the legend, so the reader matches a legend entry to a line by shape as well
// as by colour (docs/shape-uc-91-series-and-movements.md §2 D5).
import type { MarkerShape } from './stock-series';

type Point = readonly [number, number];

function plus(r: number): Point[] {
  const w = r * 0.42;
  const e = r * 1.1;
  return [
    [-w, -e],
    [w, -e],
    [w, -w],
    [e, -w],
    [e, w],
    [w, w],
    [w, e],
    [-w, e],
    [-w, w],
    [-e, w],
    [-e, -w],
    [-w, -w],
  ];
}

function star(r: number): Point[] {
  const outer = r * 1.3;
  const inner = outer * 0.5;
  return Array.from({ length: 10 }, (_, index) => {
    const radius = index % 2 === 0 ? outer : inner;
    const angle = -Math.PI / 2 + (index * Math.PI) / 5;
    return [radius * Math.cos(angle), radius * Math.sin(angle)] as const;
  });
}

// The outline of every shape but the circle, centred on 0,0, sized so the shapes
// read as the same weight at the same `r`.
function outline(shape: Exclude<MarkerShape, 'circle'>, r: number): Point[] {
  switch (shape) {
    case 'square': {
      const h = r * 0.85;
      return [
        [-h, -h],
        [h, -h],
        [h, h],
        [-h, h],
      ];
    }
    case 'triangle':
      return [
        [0, -r * 1.15],
        [r * 1.05, r * 0.75],
        [-r * 1.05, r * 0.75],
      ];
    case 'triangle-down':
      return [
        [0, r * 1.15],
        [r * 1.05, -r * 0.75],
        [-r * 1.05, -r * 0.75],
      ];
    case 'diamond': {
      const d = r * 1.2;
      return [
        [0, -d],
        [d, 0],
        [0, d],
        [-d, 0],
      ];
    }
    case 'plus':
      return plus(r);
    case 'cross':
      return plus(r).map(([x, y]) => [(x - y) / Math.SQRT2, (x + y) / Math.SQRT2] as const);
    case 'star':
      return star(r);
  }
}

interface SeriesMarkerProps {
  shape: MarkerShape;
  x: number;
  y: number;
  r: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
}

export function SeriesMarker({
  shape,
  x,
  y,
  r,
  fill,
  stroke,
  strokeWidth,
}: SeriesMarkerProps): JSX.Element {
  if (shape === 'circle') {
    return <circle cx={x} cy={y} r={r} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
  }
  const points = outline(shape, r)
    .map(([px, py]) => `${(x + px).toFixed(2)},${(y + py).toFixed(2)}`)
    .join(' ');
  return (
    <polygon
      points={points}
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinejoin="round"
    />
  );
}

// The legend's swatch: the series' line with its marker on it, in its colour. A
// series not on the chart has no marker, so it shows its line alone.
export function SeriesGlyph({
  shape,
  color,
}: {
  shape: MarkerShape | null;
  color: string;
}): JSX.Element {
  return (
    // eslint-disable-next-line no-restricted-syntax -- a chart legend glyph (the series' line and marker), not an icon: MudIcon draws AGE's icon set only
    <svg className="simsm-stockchart__glyph" viewBox="0 0 22 12" aria-hidden="true">
      <line x1={0} y1={6} x2={22} y2={6} stroke={color} strokeWidth={2} />
      {shape ? (
        <SeriesMarker
          shape={shape}
          x={11}
          y={6}
          r={4}
          fill={color}
          stroke={color}
          strokeWidth={0}
        />
      ) : null}
    </svg>
  );
}
